import { forwardRef, ReactNode } from 'react';

import { cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'suffix'> {
  error?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  containerClassName?: string;
}

const inputVariants = cva(
  'flex h-[41px] w-full gap-2 rounded border-2 border-grey-3 p-2 text-sm placeholder:text-grey-5 focus-visible:border-default-blue disabled:opacity-50 focus-visible:outline-none bg-white',
  {
    variants: {
      error: {
        true: 'border-default-red',
      },
      hasPrefix: {
        true: 'pl-6',
      },
      hasSuffix: {
        true: 'pr-6',
      },
    },
  },
);

const Input = forwardRef<HTMLInputElement, InputProps>(({ className, type, error, prefix, suffix, containerClassName, disabled, ...props }, ref) => {
  return (
    <div className={cn('relative flex w-full items-center', containerClassName)}>
      {prefix && <div className="left-1.4 absolute flex items-center">{prefix}</div>}
      <input
        type={type}
        className={cn(
          inputVariants({
            error,
            hasPrefix: Boolean(prefix),
            hasSuffix: Boolean(suffix),
          }),
          className,
          disabled && 'bg-grey-1 text-default-text cursor-not-allowed disabled:opacity-100',
        )}
        ref={ref}
        disabled={disabled}
        {...props}
      />
      {suffix && <div className="right-1.4 absolute flex items-center">{suffix}</div>}
    </div>
  );
});
Input.displayName = 'Input';

export { Input, inputVariants };
